/**
* UnzipRequest.js
* 2020-01-27
* @author reinier@ranj.nl
**/

/**
 * 
 * When an item from a zip is requested and the zip is not yet loaded, the zip is loaded first and stored in the zipManager.
 * 
 * When an item is retrieved from zip, its data is removed from memory.
 * When all items from a zip have been requested (=unzipped), the zip data is disposed of.
 * 
 */

class UnzipRequest extends createjs.AbstractRequest {

    constructor(item, zipManager, contentType, resultFormatter, tag) {
        super(item);
        this._cancelled = false;
        this._success = false;
        this._zipManager = zipManager;
        this._resultFormatter = resultFormatter;
        this._contentType = contentType;
        this._tag = tag;

        this._src = item.src.split("?")[0];
    }

    getResult(raw) {
        if (raw && this._rawResponse) {
            return this._rawResponse;
        }
        return this._response;
    }

    // Overrides abstract method in AbstractRequest
    destroy() {
        this.cancel();
    }

    // Overrides abstract method in AbstractRequest
    cancel() {
        this._cancelled = true;
        this.dispatchEvent(new createjs.Event("abort"));
    }

    // Overrides abstract method in AbstractLoader
    load() {
        //retrieve data from zip
        this._zipManager.getZip(this._item.bundle, this._onZipLoadProgress.bind(this))
            .then(this._unzipItem.bind(this))
            .then(this._onUnzippedItem.bind(this))
            .catch(this._onError.bind(this))
            .finally(() => {
                //We dispatch the event in finally, instead of in the last 'then' clause
                // because the event may a lot of code to the stack, 
                // which we do not want to be executed within the promise chain,
                // which we do not want because if an error occurs during that execution, 
                // that error would be caught by the .catch() of the promise chain. 
                if (this._success) {
                    //use timeout to guarantee we're executing outside of current stack
                    setTimeout(() => {
                        this.dispatchEvent(new createjs.Event("complete"));
                    });
                }                
            })
    }

    _onZipLoadProgress(event) {
        var newEvent = new createjs.ProgressEvent(event.loaded, event.total);
        this.dispatchEvent(newEvent);
    }

    toString() {
        return "[UnzipRequest '" + this._src + "' in bundle '" + this._item.bundle + "']";
    }


    _unzipItem(zip) {
        //wrapped in promise so we can pass on zip as result, for chaining promises
        return new Promise((resolve, reject) => {
            this._zipManager.getFromZip(this._item.bundle, this._src, this._contentType)
                .then(data => {
                    if (this._cancelled) {
                        reject("UnzipRequest cancelled");
                    }
                    this._rawResponse = data;
                    this._response = this._resultFormatter != null ? this._resultFormatter(data, this._item, this._tag) : data;
                    this._success = true;
                    resolve(zip);
                })
        })
    }

    _onUnzippedItem(zip) {
        //remove data from zip to reduce memory usage
        return this._zipManager.removeFromZip(this._item.bundle, this._src);
    }

    _onError(err) {
        this.dispatchEvent(new createjs.ErrorEvent(err));
    }
}

export default UnzipRequest;